import FormsTabs from '../components/FormsTabs';
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import './FormsBeheer.css';

const BEHEER_ROLLEN = ['super_admin', 'hoofdbeheerder', 'bedrijf_admin', 'werkvoorbereider'];

const FormsBeheer = () => {
  const { gebruiker } = useAuth();
  const navigate = useNavigate();

  const [templates, setTemplates] = useState([]);
  const [categorieen, setCategorieen] = useState([]);
  const [rollen, setRollen] = useState([]);
  const [rollenOpenId, setRollenOpenId] = useState(null);
  const [actieMenuId, setActieMenuId] = useState(null); // FORMS_V2
  const publiekeLink = async (t, uit) => { // FORMS_V2C
    try {
      const r = await api.post('/forms-workflow/templates/' + t.id + '/publiek', { uit: !!uit });
      if (uit) { alert('Publieke link uitgezet.'); return; }
      const url = r.data.url;
      try { await navigator.clipboard.writeText(url); } catch (_) {}
      window.prompt('Publieke invullink (gekopieerd). Iedere bezoeker start een nieuw formulier; inzendingen komen in de inbox. Deel deze link op je website of per mail:', url);
    } catch (e) { alert(e.response?.data?.message || e.message); }
  };
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterTekst, setFilterTekst] = useState('');
  const [toonInactief, setToonInactief] = useState(false);
  const [activeCategorie, setActiveCategorie] = useState('alle');

  const [showUpload, setShowUpload] = useState(false);
  const [upNaam, setUpNaam] = useState('');
  const [upBeschr, setUpBeschr] = useState('');
  const [upCategorie, setUpCategorie] = useState('');
  const [upFile, setUpFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [upError, setUpError] = useState(null);
  const fileInputRef = useRef(null);

  const [showCatBeheer, setShowCatBeheer] = useState(false);
  // Versturen-modal state
  const [showVerstuur, setShowVerstuur] = useState(false);
  const [verstuurTemplate, setVerstuurTemplate] = useState(null);
  const [verstuurInfo, setVerstuurInfo] = useState(null);
  const [verstuurGroepen, setVerstuurGroepen] = useState([[]]);
  const [verstuurLoading, setVerstuurLoading] = useState(false);
  const [verstuurError, setVerstuurError] = useState(null);
  const [verstuurResult, setVerstuurResult] = useState(null);
  const [verstuurOnderwerp, setVerstuurOnderwerp] = useState('');
  const [verstuurBericht, setVerstuurBericht] = useState('');
  const [klanten, setKlanten] = useState([]);
  const [klantenCategorie, setKlantenCategorie] = useState('');
  const [geselecteerdeKlant, setGeselecteerdeKlant] = useState(null);
  const [stuurNaarPartner, setStuurNaarPartner] = useState(false);
  const [verstuurType, setVerstuurType] = useState('klant');
  const [personeel, setPersoneel] = useState([]);
  const [geselecteerdPersoneel, setGeselecteerdPersoneel] = useState(null);

  // Klanten ophalen voor klant-selector in versturen-modal
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    fetch('/api/klanten', { headers: { 'Authorization': 'Bearer ' + token } })
      .then(r => r.ok ? r.json() : [])
      .then(d => setKlanten(Array.isArray(d) ? d : (d.data || d.klanten || [])))
      .catch(() => {});
    fetch('/api/gebruikers', { headers: { 'Authorization': 'Bearer ' + token } })
      .then(r => r.ok ? r.json() : [])
      .then(d => setPersoneel(Array.isArray(d) ? d : (d.data || d.gebruikers || [])))
      .catch(() => {});
  }, []);

  // Reset klant-selectie bij modal-close
  useEffect(() => {
    if (!showVerstuur) {
      setGeselecteerdeKlant(null);
      setGeselecteerdPersoneel(null);
      setVerstuurType('klant');
      setStuurNaarPartner(false);
      setKlantenCategorie('');
    }
  }, [showVerstuur]);

  const magBeheren = gebruiker && BEHEER_ROLLEN.includes(gebruiker.rol);

  useEffect(() => { laadAlles(); }, []);

  const laadAlles = async () => {
    setLoading(true);
    setError(null);
    try {
      const [tplRes, catRes] = await Promise.all([
        api.get('/form-templates'),
        api.get('/form-categorieen')
      ]);
      setTemplates(tplRes.data.templates || []);
      setCategorieen(catRes.data.categorieen || []);
      // Vaste invul-rollen (id's kloppen met de rollen-tabel). Fallback als /rollen niet toegankelijk is.
      const VASTE_ROLLEN = [
        { id: 3, naam: 'monteur' },
        { id: 16, naam: 'uitvoerder' },
        { id: 2, naam: 'werkvoorbereider' },
        { id: 8, naam: 'manager' },
      ];
      try {
        const rolRes = await api.get('/rollen');
        const alle = rolRes.data.rollen || rolRes.data.data || rolRes.data || [];
        const relevant = alle.filter(r => ['monteur','uitvoerder','werkvoorbereider','manager'].includes(r.naam));
        setRollen(relevant.length ? relevant : VASTE_ROLLEN);
      } catch (e) {
        setRollen(VASTE_ROLLEN); // /rollen niet toegankelijk → gebruik vaste lijst
      }
    } catch (err) {
      console.error('Fout bij laden:', err);
      const msg = err.response?.data?.message || 'Fout bij ophalen';
      setError(msg);
      if (err.response?.status === 403) {
        setError(msg + ' — Forms-licentie staat mogelijk uit voor dit bedrijf.');
      }
    } finally {
      setLoading(false);
    }
  };

  const openUploadModal = () => {
    setUpNaam(''); setUpBeschr(''); setUpCategorie('');
    setUpFile(null); setUpError(null); setShowUpload(true);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    setUpError(null);
    if (!upNaam.trim()) { setUpError('Naam is verplicht'); return; }
    if (!upFile) { setUpError('Selecteer een bestand'); return; }
    if (!/\.(pdf|docx|doc|xlsx|xls|pptx|ppt|odt|ods|odp)$/i.test(upFile.name)) { setUpError('Alleen PDF, Word, Excel, PowerPoint of OpenDocument bestanden zijn toegestaan'); return; }
    if (upFile.size > 20 * 1024 * 1024) { setUpError('Bestand mag max 20MB zijn'); return; }
    const fd = new FormData();
    fd.append('naam', upNaam.trim());
    if (upBeschr.trim()) fd.append('beschrijving', upBeschr.trim());
    if (upCategorie) fd.append('categorie_id', upCategorie);
    fd.append('pdf', upFile);
    const isExcel = /\.(xlsx|xls)$/i.test(upFile.name);
    setUploading(true);
    try {
      const res = await api.post('/form-templates', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 90000  // Excel conversie + auto-detect kan langer duren
      });
      setShowUpload(false);
      // Toon success met aantal auto-gedetecteerde velden
      if (isExcel && res.data?.auto_fields_count > 0) {
        setTimeout(() => alert(`✓ Template aangemaakt met ${res.data.auto_fields_count} auto-gedetecteerde velden.\n\nGa naar de Editor om velden te finetunen.`), 100);
      } else if (isExcel && res.data?.auto_detect_error) {
        setTimeout(() => alert(`Template aangemaakt, maar auto-detectie mislukte:\n${res.data.auto_detect_error}\n\nJe kunt velden handmatig toevoegen via de Editor.`), 100);
      }
      await laadAlles();
    } catch (err) {
      setUpError(err.response?.data?.message || 'Fout bij upload');
    } finally {
      setUploading(false);
    }
  };

  const handleHernoem = async (template) => {
    const nieuw = window.prompt('Nieuwe naam:', template.naam);
    if (!nieuw || nieuw.trim() === template.naam) return;
    try {
      await api.put('/form-templates/' + template.id, { naam: nieuw.trim() });
      await laadAlles();
    } catch (err) { alert('Fout: ' + (err.response?.data?.message || err.message)); }
  };

  const handleZetRollen = async (template, rolId, aan) => {
    const huidig = Array.isArray(template.zichtbaar_voor_rollen) ? template.zichtbaar_voor_rollen.map(Number) : [];
    const nieuw = aan ? [...new Set([...huidig, rolId])] : huidig.filter(id => id !== rolId);
    // optimistisch updaten
    setTemplates(prev => prev.map(t => t.id === template.id ? { ...t, zichtbaar_voor_rollen: nieuw } : t));
    try {
      await api.put('/form-templates/' + template.id, { zichtbaar_voor_rollen: nieuw });
    } catch (e) { alert('Opslaan mislukt'); laadAlles(); }
  };

  const handleZetCategorie = async (template, categorieId) => {
    try {
      await api.put('/form-templates/' + template.id, {
        categorie_id: categorieId === '' ? null : parseInt(categorieId, 10)
      });
      await laadAlles();
    } catch (err) { alert('Fout: ' + (err.response?.data?.message || err.message)); }
  };

  const handleHardDelete = async (template) => {
    const naam = template.naam;
    if (!window.confirm(`⚠️ HARD DELETE\n\nWil je "${naam}" PERMANENT verwijderen?\n\nDit verwijdert:\n• Het template zelf\n• Alle velden\n• Alle ingevulde formulieren\n\nDit is NIET te ondoen!`)) return;
    if (!window.confirm(`Echt zeker? Type "ja" zou hier helpen, maar deze keer doet 1x extra confirm het.\n\nVerwijder "${naam}" permanent?`)) return;
    try {
      await api.delete('/form-templates/' + template.id + '/hard');
      await laadAlles();
    } catch (err) {
      alert('Fout: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleArchiveer = async (template) => {
    if (!window.confirm('"' + template.naam + '" archiveren?')) return;
    try {
      await api.delete('/form-templates/' + template.id);
      await laadAlles();
    } catch (err) { alert('Fout: ' + (err.response?.data?.message || err.message)); }
  };

  const handleHeractiveer = async (template) => {
    try {
      await api.put('/form-templates/' + template.id, { actief: true });
      await laadAlles();
    } catch (err) { alert('Fout: ' + (err.response?.data?.message || err.message)); }
  };

  const handleNieuweInvulling = async (template) => {
    try {
      const res = await api.post('/form-submissions', { template_id: template.id });
      const subId = res.data.submission?.id;
      if (subId) navigate('/forms/invullen/' + subId);
    } catch (err) { alert('Fout: ' + (err.response?.data?.message || err.message)); }
  };

  const filterByCategorie = (t) => {
    if (activeCategorie === 'alle') return true;
    if (activeCategorie === 'geen') return !t.categorie_id;
    return t.categorie_id === parseInt(activeCategorie, 10);
  };

  const filtered = templates.filter(t => {
    if (!toonInactief && !t.actief) return false;
    if (!filterByCategorie(t)) return false;
    if (filterTekst.trim()) {
      const q = filterTekst.toLowerCase();
      return (t.naam || '').toLowerCase().includes(q)
          || (t.beschrijving || '').toLowerCase().includes(q)
          || (t.categorie_naam || '').toLowerCase().includes(q);
    }
    return true;
  });

  const actieveTpls = templates.filter(t => t.actief);
  const countAlle = actieveTpls.length;
  const countGeen = actieveTpls.filter(t => !t.categorie_id).length;
  const countByCat = (catId) => actieveTpls.filter(t => t.categorie_id === catId).length;


  const verstuurOpen = async (template) => {
    setVerstuurError(null);
    setVerstuurResult(null);
    setVerstuurInfo(null);
    setVerstuurTemplate(template);
    setVerstuurGroepen([[]]);
    setShowVerstuur(true);
    try {
      const res = await fetch(`/api/forms-workflow/template-info/${template.id}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || 'Kan template-info niet laden');
      if (data.template.aantal_sig_velden === 0) {
        setVerstuurError('Deze template heeft geen handtekening-velden — bewerk via "Velden" om er een toe te voegen.');
        setVerstuurInfo(data.template);
        return;
      }
      setVerstuurInfo(data.template);
      const lege = Array.from({ length: data.template.aantal_sig_velden }, () => ({ naam: '', email: '' }));
      setVerstuurGroepen([lege]);
    } catch (e) {
      setVerstuurError(e.message);
    }
  };

  const verstuurAddGroep = () => {
    if (!verstuurInfo) return;
    const lege = Array.from({ length: verstuurInfo.aantal_sig_velden }, () => ({ naam: '', email: '' }));
    setVerstuurGroepen([...verstuurGroepen, lege]);
  };

  // FORMS_V2D: bulk via CSV/plak — per regel: naam,email[,telefoon]; bij meerdere handtekeningvelden: naam1,email1,naam2,email2,…
  const verstuurImportCsv = (text) => {
    if (!verstuurInfo) return;
    const n = verstuurInfo.aantal_sig_velden || 1;
    const regels = String(text || '').split(/\r?\n/).map(r => r.trim()).filter(Boolean);
    const groepen = [];
    let overgeslagen = 0;
    for (const regel of regels) {
      if (/^naam[,;\t]/i.test(regel)) continue; // kopregel
      const cols = regel.split(/[;,\t]/).map(c => c.trim().replace(/^"|"$/g, ''));
      const groep = [];
      for (let i = 0; i < n; i++) {
        const naam = cols[i * 2] || '', email = cols[i * 2 + 1] || '';
        if (!naam || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { groep.length = 0; break; }
        groep.push({ naam, email });
      }
      if (groep.length === n) groepen.push(groep); else overgeslagen++;
    }
    if (!groepen.length) { alert('Geen geldige regels gevonden. Formaat per regel: naam,email' + (n > 1 ? ' (× ' + n + ' ondertekenaars)' : '')); return; }
    setVerstuurGroepen(prev => {
      const bestaand = prev.filter(g => g.some(p => p.naam || p.email));
      return [...bestaand, ...groepen];
    });
    alert(`${groepen.length} ontvanger(s) toegevoegd${overgeslagen ? `, ${overgeslagen} regel(s) overgeslagen` : ''}.`);
  };
  const verstuurCsvFile = (file) => {
    if (!file) return;
    const r = new FileReader();
    r.onload = () => verstuurImportCsv(String(r.result || ''));
    r.readAsText(file);
  };

  const verstuurRemoveGroep = (idx) => {
    setVerstuurGroepen(verstuurGroepen.filter((_, i) => i !== idx));
  };

  const verstuurUpdate = (gi, pi, field, value) => {
    setVerstuurGroepen(verstuurGroepen.map((g, x) =>
      x !== gi ? g : g.map((p, y) => y === pi ? { ...p, [field]: value } : p)
    ));
  };

  const verstuurSubmit = async () => {
    setVerstuurLoading(true);
    setVerstuurError(null);
    try {
      for (const [gi, groep] of verstuurGroepen.entries()) {
        for (const [pi, p] of groep.entries()) {
          if (!p.naam.trim() || !p.email.trim()) {
            throw new Error(`PDF ${gi + 1} persoon ${pi + 1}: vul naam en email in`);
          }
          if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(p.email.trim())) {
            throw new Error(`PDF ${gi + 1} persoon ${pi + 1}: ongeldig email`);
          }
        }
      }
      const res = await fetch(`/api/forms-workflow/template-versturen/${verstuurTemplate.id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ groepen: verstuurGroepen, onderwerp: verstuurOnderwerp.trim() || undefined, bericht: verstuurBericht.trim() || undefined, klant_id: verstuurType === 'klant' && geselecteerdeKlant && geselecteerdeKlant.id ? geselecteerdeKlant.id : null, voor_gebruiker_id: verstuurType === 'personeel' && geselecteerdPersoneel && geselecteerdPersoneel.id ? geselecteerdPersoneel.id : null })
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || 'Versturen mislukt');
      setVerstuurResult(data);
      laadAlles();
    } catch (e) {
      setVerstuurError(e.message);
    } finally {
      setVerstuurLoading(false);
    }
  };
  return (
    <div className="forms-beheer">
      <div className="fb-header">
        <div>
          <FormsTabs active="templates" title="Templates" />
          <p className="fb-sub">Formulier-templates beheren — uploaden, velden tekenen, invullen</p>
        </div>
        <div className="fb-header-acties">
          {magBeheren && <button className="fb-btn" onClick={() => setShowCatBeheer(true)}>🏷 Categorieën</button>}
          {magBeheren && <button className="fb-btn fb-btn-primary" onClick={openUploadModal}>+ Nieuw template</button>}
        </div>
      </div>

      <div className="fb-tabs">
        <button className={'fb-tab ' + (activeCategorie === 'alle' ? 'active' : '')} onClick={() => setActiveCategorie('alle')}>
          Alle <span className="fb-tab-count">{countAlle}</span>
        </button>
        {categorieen.map(c => (
          <button key={c.id} className={'fb-tab ' + (activeCategorie === String(c.id) ? 'active' : '')}
            onClick={() => setActiveCategorie(String(c.id))}>
            <span className="fb-tab-dot" style={{ background: c.kleur }} />
            {c.naam} <span className="fb-tab-count">{countByCat(c.id)}</span>
          </button>
        ))}
        {countGeen > 0 && (
          <button className={'fb-tab ' + (activeCategorie === 'geen' ? 'active' : '')} onClick={() => setActiveCategorie('geen')}>
            Zonder categorie <span className="fb-tab-count">{countGeen}</span>
          </button>
        )}
      </div>

      <div className="fb-toolbar">
        <input type="text" placeholder="Zoeken op naam of beschrijving…"
          value={filterTekst} onChange={(e) => setFilterTekst(e.target.value)} className="fb-search" />
        <label className="fb-checkbox">
          <input type="checkbox" checked={toonInactief} onChange={(e) => setToonInactief(e.target.checked)} />
          Toon gearchiveerd
        </label>
      </div>

      {loading && <div className="fb-state">Laden…</div>}
      {error && <div className="fb-state fb-error">⚠️ {error}</div>}

      {!loading && !error && filtered.length === 0 && (
        <div className="fb-state fb-empty">
          {templates.length === 0 ? 'Nog geen templates. Klik op "Nieuw template" om je eerste PDF te uploaden.' : 'Geen templates gevonden voor deze filter.'}
        </div>
      )}

      {!loading && !error && filtered.length > 0 && (
        <table className="fb-table">
          <thead>
            <tr>
              <th>Naam</th><th>Categorie</th><th>Voor wie</th><th>Type</th><th>Versie</th>
              <th>Velden</th><th>Submissions</th><th>Datum</th><th>Status</th><th>Acties</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(t => (
              <tr key={t.id} className={!t.actief ? 'fb-row-inactief' : ''}>
                <td>
                  <strong>{t.naam}{t.signing_strength === "aes" && <span className="fb-aes-badge" title="AES handtekening vereist" style={{marginLeft:6,fontSize:14}}>🔐</span>}</strong>
                   {t.aantal_compleet > 0 && (
                    <span title={`${t.aantal_compleet} keer compleet getekend`} style={{marginLeft:'8px',padding:'2px 8px',borderRadius:'12px',background:'#d1fae5',color:'#065f46',fontSize:'11px',fontWeight:600,whiteSpace:'nowrap',verticalAlign:'middle'}}>
                      ✍ ×{t.aantal_compleet}
                    </span>
                  )}
                  {t.beschrijving && <div className="fb-beschr">{t.beschrijving}</div>}
                </td>
                <td>
                  {magBeheren ? (
                    <select className="fb-cat-select" value={t.categorie_id || ''}
                      onChange={(e) => handleZetCategorie(t, e.target.value)}
                      style={t.categorie_kleur ? { borderColor: t.categorie_kleur, color: t.categorie_kleur } : {}}>
                      <option value="">—</option>
                      {categorieen.map(c => <option key={c.id} value={c.id}>{c.naam}</option>)}
                    </select>
                  ) : t.categorie_naam ? (
                    <span className="fb-cat-badge" style={{ background: t.categorie_kleur, color: 'white' }}>{t.categorie_naam}</span>
                  ) : '—'}
                </td>
                <td style={{ position: 'relative', zIndex: rollenOpenId === t.id ? 9998 : 'auto' }}>
                  {magBeheren ? (() => {
                    const sel = Array.isArray(t.zichtbaar_voor_rollen) ? t.zichtbaar_voor_rollen.map(Number) : [];
                    const namen = rollen.filter(r => sel.includes(r.id)).map(r => r.naam);
                    return (
                      <>
                        <button type="button" onClick={() => setRollenOpenId(rollenOpenId === t.id ? null : t.id)}
                          style={{ padding: '4px 8px', border: '1px solid #d1d5db', borderRadius: 6, background: '#fff', cursor: 'pointer', fontSize: 12, minWidth: 90, textAlign: 'left' }}>
                          {namen.length ? namen.join(', ') : '— niemand —'} ▾
                        </button>
                        {rollenOpenId === t.id && (
                          <div style={{ position: 'absolute', zIndex: 9999, bottom: '100%', left: 0, marginBottom: 4, background: '#fff', border: '1px solid #d1d5db', borderRadius: 8, boxShadow: '0 -4px 16px rgba(0,0,0,0.15)', padding: 8, minWidth: 160 }}>
                            {rollen.map(r => (
                              <label key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 6px', fontSize: 13, cursor: 'pointer' }}>
                                <input type="checkbox" checked={sel.includes(r.id)}
                                  onChange={(e) => handleZetRollen(t, r.id, e.target.checked)} />
                                {r.naam}
                              </label>
                            ))}
                            <div style={{ borderTop: '1px solid #eee', marginTop: 4, paddingTop: 4, textAlign: 'right' }}>
                              <button type="button" onClick={() => setRollenOpenId(null)} style={{ fontSize: 12, background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer' }}>sluiten</button>
                            </div>
                          </div>
                        )}
                      </>
                    );
                  })() : (
                    (Array.isArray(t.zichtbaar_voor_rollen) && t.zichtbaar_voor_rollen.length)
                      ? <span style={{ fontSize: 12, color: '#6b7280' }}>{rollen.filter(r => t.zichtbaar_voor_rollen.map(Number).includes(r.id)).map(r => r.naam).join(', ')}</span>
                      : '—'
                  )}
                </td>
                <td><span className="fb-tag">{t.bron_type?.toUpperCase()}</span></td>
                <td>v{t.versie_nummer}</td>
                <td>{t.aantal_velden || 0}</td>
                <td>{t.aantal_submissions || 0}</td>
                <td>{t.created_at ? new Date(t.created_at).toLocaleDateString('nl-NL') : '—'}</td>
                <td>{t.actief ? <span className="fb-status fb-status-actief">Actief</span> : <span className="fb-status fb-status-inactief">Gearchiveerd</span>}</td>
                <td className="fb-acties" style={{ whiteSpace: 'nowrap', position: 'relative' }}>
                  {/* FORMS_V2: compacte acties */}
                  {magBeheren && t.actief && (
                    <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                      <button className="fb-btn fb-btn-sm" onClick={() => navigate('/forms/editor/' + t.id)} title="Velden tekenen op de PDF">✎ Velden</button>
                      <button className="fb-btn fb-btn-sm" onClick={() => verstuurOpen(t)} title="Versturen naar ondertekenaars">📧 Versturen</button>
                      <button className="fb-btn fb-btn-sm" onClick={() => setActieMenuId(actieMenuId === t.id ? null : t.id)} title="Meer acties" style={{ minWidth: 32 }}>⋯</button>
                      {actieMenuId === t.id && (
                        <div onMouseLeave={() => setActieMenuId(null)}
                          style={{ position: 'absolute', right: 8, top: '100%', zIndex: 50, background: '#fff', border: '1px solid #d1d5db', borderRadius: 8, boxShadow: '0 4px 16px rgba(0,0,0,0.15)', minWidth: 190, padding: 4 }}>
                          {[
                            ['✏ Hernoemen', () => handleHernoem(t), '#1e293b'],
                            ['📂 Invullen (nieuw)', () => handleNieuweInvulling(t), '#1e293b'],
                            ['🌐 Publieke invullink maken', () => publiekeLink(t, false), '#1e293b'],
                            ['🌐 Publieke link uitzetten', () => publiekeLink(t, true), '#64748b'],
                            ['🗄 Archiveren', () => handleArchiveer(t), '#92400e'],
                            ['🗑 Permanent verwijderen', () => handleHardDelete(t), '#991b1b']
                          ].map(([l, fn, c]) => (
                            <button key={l} type="button" onClick={() => { setActieMenuId(null); fn(); }}
                              style={{ display: 'block', width: '100%', textAlign: 'left', padding: '7px 10px', fontSize: 13, background: 'transparent', border: 'none', cursor: 'pointer', color: c, borderRadius: 6 }}
                              onMouseEnter={e => e.currentTarget.style.background = '#f1f5f9'} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                              {l}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                  {magBeheren && !t.actief && <button className="fb-btn fb-btn-sm" onClick={() => handleHeractiveer(t)}>↩ Heractiveren</button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {showUpload && (
        <div className="fb-modal-overlay">
          <div className="fb-modal" onClick={(e) => e.stopPropagation()}>
            <h2>Nieuw template uploaden</h2>
            <form onSubmit={handleUpload}>
              <label className="fb-label">
                Naam <span className="fb-req">*</span>
                <input type="text" value={upNaam} onChange={(e) => setUpNaam(e.target.value)}
                  placeholder="Bijv. Inbedrijfstelling H&K wisselsteller" maxLength={200} required />
              </label>
              <label className="fb-label">
                Categorie
                <select value={upCategorie} onChange={(e) => setUpCategorie(e.target.value)}>
                  <option value="">— geen —</option>
                  {categorieen.map(c => <option key={c.id} value={c.id}>{c.naam}</option>)}
                </select>
              </label>
              <label className="fb-label">
                Beschrijving
                <textarea value={upBeschr} onChange={(e) => setUpBeschr(e.target.value)}
                  placeholder="Optioneel — wat is dit formulier en wanneer wordt het gebruikt?" rows={3} />
              </label>
              <label className="fb-label">
                Document <span className="fb-req">*</span>
                <input ref={fileInputRef} type="file" accept="application/pdf,.pdf,.docx,.doc,.xlsx,.xls,.pptx,.ppt,.odt,.ods,.odp"
                  onChange={(e) => setUpFile(e.target.files?.[0] || null)} required />
                <small>Max 20MB · PDF, Word, Excel, PowerPoint, OpenDocument. Office-bestanden worden automatisch naar PDF geconverteerd.</small>
              </label>
              {upError && <div className="fb-modal-error">⚠️ {upError}</div>}
              <div className="fb-modal-acties">
                <button type="button" className="fb-btn" onClick={() => setShowUpload(false)} disabled={uploading}>Annuleren</button>
                <button type="submit" className="fb-btn fb-btn-primary" disabled={uploading}>{uploading ? 'Uploaden…' : 'Uploaden'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showCatBeheer && <CategorieBeheerModal categorieen={categorieen} onClose={() => setShowCatBeheer(false)} onChange={laadAlles} />}
      {showVerstuur && (
        <div className="fb-modal-overlay">
          <div className="fb-modal" onClick={e => e.stopPropagation()} style={{maxWidth: 720}}>
            <h3>📧 Versturen: {verstuurTemplate?.naam}</h3>
            {!verstuurInfo && !verstuurError && <p>Laden...</p>}
            {verstuurError && (
              <div style={{padding: 10, background: '#fee2e2', color: '#991b1b', borderRadius: 6, marginBottom: 12}}>{verstuurError}</div>
            )}
            {verstuurResult && (
              <div style={{padding: 12, background: '#d1fae5', color: '#065f46', borderRadius: 6, marginBottom: 12}}>✅ {verstuurResult.message}</div>
            )}
            {verstuurInfo && verstuurInfo.aantal_sig_velden > 0 && !verstuurResult && (
              <>
                <p style={{color: '#666', marginBottom: 16}}>
                  Deze template heeft <strong>{verstuurInfo.aantal_sig_velden}</strong> handtekening-veld{verstuurInfo.aantal_sig_velden > 1 ? 'en' : ''} per PDF.
                  {verstuurGroepen.length > 1 && ` Er worden ${verstuurGroepen.length} PDFs verstuurd.`}
                </p>
                <div style={{marginBottom: 16, padding: 12, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6}}>
                  <label style={{display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4, color: '#475569'}}>
                    📧 Email-onderwerp <span style={{fontWeight: 400, color: '#94a3b8'}}>(optioneel)</span>
                  </label>
                  <input type="text" value={verstuurOnderwerp} onChange={e => setVerstuurOnderwerp(e.target.value)}
                    placeholder="Bijv: 'Werkbon klaar voor ondertekening'"
                    style={{width: '100%', padding: 8, border: '1px solid #cbd5e1', borderRadius: 4, marginBottom: 10, fontFamily: 'inherit', fontSize: 14}} />
                  <label style={{display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4, color: '#475569'}}>
                    💬 Persoonlijk bericht <span style={{fontWeight: 400, color: '#94a3b8'}}>(optioneel)</span>
                  </label>
                  <textarea value={verstuurBericht} onChange={e => setVerstuurBericht(e.target.value)}
                    placeholder="Bijv: 'Hoi! Wil je dit even ondertekenen? Vragen? Bel me gerust.'"
                    rows={3}
                    style={{width: '100%', padding: 8, border: '1px solid #cbd5e1', borderRadius: 4, fontFamily: 'inherit', fontSize: 14, resize: 'vertical'}} />
                  <div style={{fontSize: 11, color: '#94a3b8', marginTop: 4}}>
                    💡 Leeg laten = standaard tekst gebruiken
                  </div>
                </div>
                <div style={{marginBottom: 16, padding: 12, background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 6}}>
                  <label style={{display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 8, color: '#1e40af'}}>
                    <div style={{display:'flex',gap:6,marginBottom:4}}>
                      <button type="button" onClick={(e) => {e.preventDefault(); setVerstuurType('klant');}} style={{flex:1,padding:'6px 10px',fontSize:13,border:'1px solid #cbd5e1',borderRadius:4,background:verstuurType==='klant'?'#3b82f6':'white',color:verstuurType==='klant'?'white':'#1e293b',cursor:'pointer',fontWeight:500}}>📋 Klant</button>
                      <button type="button" onClick={(e) => {e.preventDefault(); setVerstuurType('personeel');}} style={{flex:1,padding:'6px 10px',fontSize:13,border:'1px solid #cbd5e1',borderRadius:4,background:verstuurType==='personeel'?'#3b82f6':'white',color:verstuurType==='personeel'?'white':'#1e293b',cursor:'pointer',fontWeight:500}}>👤 Personeel</button>
                    </div>
                    <span style={{fontWeight: 400, color: '#64748b', fontSize: 12}}>(vult naam + email automatisch)</span>
                  </label>
                  <div style={{display: 'flex', gap: 8, marginBottom: 8}}>
                    <select value={klantenCategorie} onChange={e => setKlantenCategorie(e.target.value)}
                      style={{padding: 8, border: '1px solid #cbd5e1', borderRadius: 4, fontSize: 14, minWidth: 140}}>
                      <option value="">Alle categorieën</option>
                      {[...new Set(klanten.map(k => k.categorie).filter(Boolean))].sort().map(cat => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                    {verstuurType === 'klant' ? (
                      <select value={geselecteerdeKlant?.id || ''} onChange={e => {
                        const k = klanten.find(x => x.id === parseInt(e.target.value));
                        setGeselecteerdeKlant(k || null);
                        setStuurNaarPartner(false);
                        if (k) {
                          const naamFill = k.contactpersoon || k.naam || '';
                          const emailFill = k.email || '';
                          const telefoonFill = k.telefoon || '';
                          setVerstuurGroepen(prev => prev.map(groep =>
                            groep.map((s, idx) => idx === 0 ? { ...s, naam: naamFill, email: emailFill, telefoon: telefoonFill } : s)
                          ));
                        }
                      }} style={{flex: 1, padding: 8, border: '1px solid #cbd5e1', borderRadius: 4, fontSize: 14}}>
                        <option value="">— Geen klant —</option>
                        {klanten
                          .filter(k => !klantenCategorie || k.categorie === klantenCategorie)
                          .map(k => (
                            <option key={k.id} value={k.id}>
                              {k.naam}{k.contactpersoon ? ' (' + k.contactpersoon + ')' : ''}{k.email ? ' — ' + k.email : ''}
                            </option>
                          ))}
                      </select>
                    ) : (
                      <select value={geselecteerdPersoneel?.id || ''} onChange={e => {
                        const m = personeel.find(x => x.id === parseInt(e.target.value));
                        setGeselecteerdPersoneel(m || null);
                        if (m) {
                          const naamFill = ((m.voornaam || '') + ' ' + (m.achternaam || '')).trim();
                          const emailFill = m.email || '';
                          const telefoonFill = m.telefoon || '';
                          setVerstuurGroepen(prev => prev.map(groep =>
                            groep.map((s, idx) => idx === 0 ? { ...s, naam: naamFill, email: emailFill, telefoon: telefoonFill } : s)
                          ));
                        }
                      }} style={{flex: 1, padding: 8, border: '1px solid #cbd5e1', borderRadius: 4, fontSize: 14}}>
                        <option value="">— Geen personeel —</option>
                        {personeel.map(m => (
                          <option key={m.id} value={m.id}>
                            {((m.voornaam || '') + ' ' + (m.achternaam || '')).trim()}{m.email ? ' — ' + m.email : ''}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                  {geselecteerdeKlant && geselecteerdeKlant.partner_naam && verstuurInfo && verstuurInfo.aantal_sig_velden >= 2 && (
                    <label style={{display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer', marginTop: 4}}>
                      <input type="checkbox" checked={stuurNaarPartner} onChange={e => {
                        const nieuw = e.target.checked;
                        setStuurNaarPartner(nieuw);
                        setVerstuurGroepen(prev => prev.map(groep => {
                          const ng = [...groep];
                          if (ng.length >= 2) {
                            ng[1] = nieuw
                              ? { ...ng[1], naam: geselecteerdeKlant.partner_naam, email: geselecteerdeKlant.partner_email || '', telefoon: geselecteerdeKlant.partner_telefoon || '' }
                              : { ...ng[1], naam: '', email: '', telefoon: '' };
                          }
                          return ng;
                        }));
                      }} />
                      Ook naar <strong>{geselecteerdeKlant.partner_naam}</strong> sturen (partner — 2e ondertekenaar)
                    </label>
                  )}
                </div>
                {/* FORMS_V2D: bulk-import */}
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10, padding: 10, background: '#f1f5f9', borderRadius: 6, fontSize: 13 }}>
                  <strong>Bulk:</strong>
                  <label className="fb-btn fb-btn-sm" style={{ cursor: 'pointer' }}>
                    📄 CSV kiezen
                    <input type="file" accept=".csv,.txt" style={{ display: 'none' }} onChange={e => { verstuurCsvFile(e.target.files[0]); e.target.value = ''; }} />
                  </label>
                  <button type="button" className="fb-btn fb-btn-sm" onClick={() => { const t = window.prompt('Plak regels (naam,email per regel):'); if (t) verstuurImportCsv(t); }}>📋 Plakken</button>
                  <span style={{ color: '#64748b' }}>naam,email{(verstuurInfo?.aantal_sig_velden || 1) > 1 ? ',naam2,email2,…' : ''} per regel · elke regel wordt één PDF</span>
                </div>
                {verstuurGroepen.map((groep, gi) => (
                  <div key={gi} style={{border: '1px solid #ddd', borderRadius: 6, padding: 12, marginBottom: 12, background: '#fafafa'}}>
                    <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8}}>
                      <strong>PDF {gi + 1}</strong>
                      {verstuurGroepen.length > 1 && (
                        <button className="fb-btn fb-btn-sm fb-btn-danger" onClick={() => verstuurRemoveGroep(gi)}>🗑</button>
                      )}
                    </div>
                    {groep.map((persoon, pi) => (
                      <div key={pi} style={{display: 'flex', gap: 8, marginBottom: 6}}>
                        <input type="text" placeholder={verstuurInfo.aantal_sig_velden > 1 ? `Naam ondertekenaar ${pi + 1}` : 'Naam'}
                          value={persoon.naam} onChange={e => verstuurUpdate(gi, pi, 'naam', e.target.value)}
                          style={{flex: 1, padding: 6, border: '1px solid #ccc', borderRadius: 4}} />
                        <input type="email" placeholder="email@adres.nl"
                          value={persoon.email} onChange={e => verstuurUpdate(gi, pi, 'email', e.target.value)}
                          style={{flex: 1, padding: 6, border: '1px solid #ccc', borderRadius: 4}} />
                        <input type="tel" placeholder="06-12345678 (SMS-OTP)"
                          value={persoon.telefoon || ''} onChange={e => verstuurUpdate(gi, pi, 'telefoon', e.target.value)}
                          style={{flex: 1, padding: 6, border: '1px solid #ccc', borderRadius: 4, minWidth: 130}} />
                      </div>
                    ))}
                  </div>
                ))}
                <button className="fb-btn fb-btn-sm" onClick={verstuurAddGroep} style={{marginBottom: 16}}>+ Nog een PDF toevoegen</button>
              </>
            )}
            <div style={{display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16}}>
              <button className="fb-btn" onClick={() => setShowVerstuur(false)} disabled={verstuurLoading}>
                {verstuurResult ? 'Sluiten' : 'Annuleren'}
              </button>
              {verstuurInfo && verstuurInfo.aantal_sig_velden > 0 && !verstuurResult && (
                <button className="fb-btn fb-btn-primary" onClick={verstuurSubmit} disabled={verstuurLoading}>
                  {verstuurLoading ? 'Versturen...' : `Verstuur ${verstuurGroepen.length} PDF${verstuurGroepen.length > 1 ? 's' : ''}`}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const CategorieBeheerModal = ({ categorieen, onClose, onChange }) => {
  const [nieuwNaam, setNieuwNaam] = useState('');
  const [nieuwKleur, setNieuwKleur] = useState('#6b7280');
  const [busy, setBusy] = useState(false);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!nieuwNaam.trim()) return;
    setBusy(true);
    try {
      await api.post('/form-categorieen', { naam: nieuwNaam.trim(), kleur: nieuwKleur });
      setNieuwNaam(''); setNieuwKleur('#6b7280');
      await onChange();
    } catch (err) { alert('Fout: ' + (err.response?.data?.message || err.message)); }
    finally { setBusy(false); }
  };

  const handleDelete = async (cat) => {
    if (!window.confirm('Categorie "' + cat.naam + '" verwijderen? Templates worden ontkoppeld.')) return;
    setBusy(true);
    try { await api.delete('/form-categorieen/' + cat.id); await onChange(); }
    catch (err) { alert('Fout: ' + (err.response?.data?.message || err.message)); }
    finally { setBusy(false); }
  };

  const handleHernoem = async (cat) => {
    const nieuw = window.prompt('Nieuwe naam:', cat.naam);
    if (!nieuw || nieuw.trim() === cat.naam) return;
    setBusy(true);
    try { await api.put('/form-categorieen/' + cat.id, { naam: nieuw.trim() }); await onChange(); }
    catch (err) { alert('Fout: ' + (err.response?.data?.message || err.message)); }
    finally { setBusy(false); }
  };

  const handleKleur = async (cat, kleur) => {
    setBusy(true);
    try { await api.put('/form-categorieen/' + cat.id, { kleur }); await onChange(); }
    catch (err) { alert('Fout: ' + (err.response?.data?.message || err.message)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fb-modal-overlay">
      <div className="fb-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 600 }}>
        <h2>🏷 Categorieën beheren</h2>
        <ul className="fb-cat-list">
          {categorieen.map(c => (
            <li key={c.id} className="fb-cat-row">
              <input type="color" value={c.kleur} onChange={(e) => handleKleur(c, e.target.value)}
                disabled={busy} title="Kleur wijzigen" className="fb-cat-color" />
              <span className="fb-cat-name">{c.naam}</span>
              {c.is_systeem && <span className="fb-cat-systeem">standaard</span>}
              <div style={{ flex: 1 }} />
              <button className="fb-btn fb-btn-sm" onClick={() => handleHernoem(c)} disabled={busy}>✏ Hernoem</button>
              <button className="fb-btn fb-btn-sm fb-btn-danger" onClick={() => handleDelete(c)} disabled={busy}>🗑</button>
            </li>
          ))}
        </ul>
        <form onSubmit={handleAdd} className="fb-cat-add">
          <h3>Nieuwe categorie toevoegen</h3>
          <div className="fb-cat-add-row">
            <input type="color" value={nieuwKleur} onChange={(e) => setNieuwKleur(e.target.value)}
              disabled={busy} className="fb-cat-color" />
            <input type="text" value={nieuwNaam} onChange={(e) => setNieuwNaam(e.target.value)}
              placeholder="Bijv. RailControl" maxLength={100} disabled={busy} />
            <button type="submit" className="fb-btn fb-btn-primary" disabled={busy || !nieuwNaam.trim()}>Toevoegen</button>
          </div>
        </form>
        <div className="fb-modal-acties" style={{ marginTop: 16 }}>
          <button type="button" className="fb-btn" onClick={onClose}>Sluiten</button>
        </div>
      </div>
    </div>
  );
};

export default FormsBeheer;
